import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSiteAssets } from '@/hooks/siteHooks';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import SiteDeviceCard from './SiteDeviceCard';

export interface SiteDevicesProps {
  siteId: string;
  companyId: string;
}

export default function SiteDevices({ siteId, companyId }: SiteDevicesProps) {
  const navigate = useNavigate();

  const {
    data: siteAssets,
    isLoading,
    error,
  } = useSiteAssets({
    siteId,
    companyId,
  });

  // Log the parameters and any errors for debugging
  console.log('SiteDevices component params:', { siteId, companyId });
  if (error) {
    console.error('Error fetching site assets:', error);
  }

  const handleAddSelection = (value: string) => {
    switch (value) {
      case 'bulk':
        navigate('/devices/bulk-add');
        break;
      case 'device':
        navigate(`/devices/customer/${companyId}/add-device?siteId=${siteId}`);
        break;
      case 'site':
        navigate('/devices/add-site');
        break;
      default:
        break;
    }
  };

  const [searchQuery, setSearchQuery] = useState('');

  const filteredList = useFuzzySearchList({
    list: siteAssets?.filter((x) => x.deletedAt == null) ?? [],
    queryText: searchQuery,
    getText: (site) => [site.name, site.serialNumber, site.groupName ?? null, site.location],
    mapResultItem: ({ item }) => item,
  });

  return (
    <>
      <div className="m-4 mb-4 flex items-center justify-between gap-4 rounded-xl border bg-card1 p-4">
        <Input
          placeholder="Search devices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          isSearch
          className="max-w-80"
        />
        <div className="flex items-center gap-4">
          <Select onValueChange={handleAddSelection} value="">
            <SelectTrigger className="w-[180px] font-semibold text-lime">
              <SelectValue placeholder="Add New Device" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="device" className="rounded-none border-b pb-2 pl-3">
                  <Link to="/devices/add-device">
                    <div className="flex items-center gap-4">
                      <img src="/icon/cctv.svg" className="h-5 w-5" />
                      <div>
                        <h3 className="font-semibold">Add Device</h3>
                        <p className="text-white">Add a single device</p>
                      </div>
                    </div>
                  </Link>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <span>Loading...</span>
        </div>
      ) : filteredList.length === 0 ? (
        <div className="mt-8 text-center text-gray-400">No devices found</div>
      ) : (
        <div className="grid w-full grid-cols-1 gap-4 p-4 pt-0 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredList
            .sort((a, b) => {
              if (!a.name && !b.name) return 0;
              if (!a.name) return 1;
              if (!b.name) return -1;
              return a.name.localeCompare(b.name);
            })
            .map((device) => (
              <div key={device.id} className="space-y-4">
                <SiteDeviceCard device={device} siteId={siteId} />
              </div>
            ))}
        </div>
      )}
    </>
  );
}
